type LocationNode = {
    id: number;
    name: string;
    parentIds: number[];
};

type LevelDefinition = {
    levelId: number;
    levelName: string;
    allPlaceholder: string;
    locations: LocationNode[];
};

type MinimalSelectionResult = {
    nodesByLevel: Map<LevelDefinition, LocationNode[]>;
}

/**
 * Allows a list of geographic area selections to be reduced down to a summary list, showing only
 * the highest-level selected nodes if all of its child nodes are selected. The remaining, partially-selected
 * nodes are also returned.
 *
 * The data is assumed to be free of loops, although this class can tolerate some.
 */
export class GeographicAreaSelectionReducer {
    private readonly _levels: LevelDefinition[];
    private readonly _selectedIds: number[];

    // Maps nodes with unique IDs to their definitions
    private _nodeMap: Map<string, LocationNode> = new Map<string, LocationNode>();

    // Maps a node's unique ID to its unique child node IDs
    private _parentChildNodesMap: Map<string, string[]>;

    constructor(levels: LevelDefinition[], selectedIds: number[]) {
        // Sort the geographic levels into order, broadest to finest
        this._levels = [...levels].sort((a, b) => b.levelId - a.levelId);

        this._selectedIds = selectedIds;

        this._buildMaps();
    }

    /**
     * Builds up the map structures that are used to process the data more efficiently.
     * This only needs to be done once on init.
     * @private
     */
    private _buildMaps(): void {
        const nodeMap: Map<string, LocationNode> = new Map<string, LocationNode>();
        const parentChildNodesMap: Map<string, string[]> = new Map<string, string[]>();

        for (let geoLevelIndex = 0; geoLevelIndex < this._levels.length; geoLevelIndex++) {
            const geographicLevel = this._levels[geoLevelIndex];
            const geographicLevelId = geographicLevel.levelId;

            for (let nodeIndex = 0; nodeIndex < geographicLevel.locations.length; nodeIndex++) {
                const node = geographicLevel.locations[nodeIndex];
                const uniqueId = this._uniqueIdFromNumeric(node.id, geographicLevelId);

                // Skip this node if we've already seen it
                if (nodeMap.has(uniqueId)) {
                    continue;
                }

                nodeMap.set(uniqueId, node);

                for (let parentIndex = 0; parentIndex < node.parentIds.length; parentIndex++) {
                    const parentId = this._uniqueIdFromNumeric(node.parentIds[parentIndex], geographicLevelId + 1);

                    this._upsertIntoArrayMap<string, string>(parentChildNodesMap, parentId, uniqueId);
                }
            }
        }

        this._nodeMap = nodeMap;
        this._parentChildNodesMap = parentChildNodesMap;
    }

    /**
     * Calculates the highest-level selections given the selected IDs. If all of a node's children are selected,
     * that node is considered selected. This continues up the tree, keeping track of partially selected nodes,
     * to produce a 'summary' that shows the highest level selected nodes.
     */
    public getHighestLevelSelections(): MinimalSelectionResult {
        let minimalSelectionIds: string[] = [];

        const topLevel = this._levels[0];

        for (let topLevelNodeIndex = 0; topLevelNodeIndex < topLevel.locations.length; topLevelNodeIndex++) {
            const topLevelNode = topLevel.locations[topLevelNodeIndex];
            const uniqueNodeId = this._uniqueIdFromNumeric(topLevelNode.id, topLevel.levelId);
            const selectedChildIds = this._getSelectedChildren(uniqueNodeId);

            minimalSelectionIds = [...minimalSelectionIds, ...selectedChildIds];
        }

        const uniqueNodeIdsByLevel: Map<number, string[]> = new Map<number, string[]>();

        minimalSelectionIds.forEach((uniqueId) => {
            const idParts = this._numericIdFromUnique(uniqueId);

            this._upsertIntoArrayMap<number, string>(uniqueNodeIdsByLevel, idParts.level, uniqueId);
        });

        const nodeDefinitionsByLevel: Map<LevelDefinition, LocationNode[]> = new Map<LevelDefinition, LocationNode[]>();

        for (let [levelId, uniqueNodeIds] of uniqueNodeIdsByLevel) {
            const levelDefinition = this._levels.find((level) => level.levelId === levelId);
            const nodes = uniqueNodeIds.map((uniqueId) => this._nodeMap.get(uniqueId));

            nodeDefinitionsByLevel.set(levelDefinition, nodes);
        }

        return {
            nodesByLevel: nodeDefinitionsByLevel,
        };
    }

    /**
     * Get which child nodes are selected for a given node.
     * @param nodeId The node ID to check.
     * @private
     * @returns A list of IDs of selected nodes.
     */
    private _getSelectedChildren(nodeId: string): string[] {
        const children = this._parentChildNodesMap.get(nodeId);
        const idParts = this._numericIdFromUnique(nodeId);

        // Base case: no children, return this node ID only if it's selected, otherwise no IDs
        if (children === undefined || children.length === 0) {
            const thisNodeSelected = this._selectedIds.includes(idParts.id);

            return thisNodeSelected ? [nodeId] : [];
        }

        // Main case: has some child nodes, return this node's selected child nodes
        let selectedChildIds: string[] = [];

        for (let childIndex: number = 0; childIndex < children.length; childIndex++) {
            const childId = children[childIndex];
            const selectedChildNodes = this._getSelectedChildren(childId);

            selectedChildIds = [...selectedChildIds, ...selectedChildNodes];
        }

        // Special case: if all children are selected, return this ID only.
        // This is the key to shortening the list of results to the highest-level selected IDs only.
        if (selectedChildIds.length === children.length && selectedChildIds.every((childId) => children.includes(childId))) {
            return [nodeId];
        }

        return selectedChildIds;
    }

    /**
     * Adds a number to a map's value (array of numbers) and adds an empty array if the key does not yet exist.
     * Does not add the value again to the array if it is already in it.
     * @param map The map to add the value to.
     * @param key The key in the map to add the value to.
     * @param value The value to add.
     * @private
     */
    private _upsertIntoArrayMap<K, V>(map: Map<K, V[]>, key: K, value: V): Map<K, V[]> {
        if (!map.has(key)) {
            map.set(key, [value]);

            return map;
        }

        const array = map.get(key);

        if (array.includes(value)) {
            return map;
        }

        map.set(key, [...array, value]);

        return map;
    }

    /**
     * Combines a node's level and numeric ID (unique per level) to a globally unique ID.
     * @param numericId The ID of the node in its level.
     * @param level The level it is in.
     * @private
     */
    private _uniqueIdFromNumeric(numericId: number, level: number): string {
        return `${level}-${numericId}`;
    }

    /**
     * Gets the level and level-unique node ID for a given unique node ID.
     * @param uniqueId The unique ID for the node.
     * @private
     */
    private _numericIdFromUnique(uniqueId: string): { level: number, id: number } {
        const parts = uniqueId.split('-');

        return {
            level: parseInt(parts[0]),
            id: parseInt(parts[1])
        };
    }
}
